import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, UserPlus, FileText, ChevronLeft, ChevronRight, LogOut, Presentation, HelpCircle, Target, Mail, Globe } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

const PortalSidebar = ({ collapsed, setCollapsed, onMobileClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLinkClick = () => {
    // Close mobile menu when a link is clicked
    if (onMobileClose) {
      onMobileClose();
    }
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/portal/dashboard' },
    { icon: Target, label: 'Inbound Leads', path: '/portal/admin/leads', adminOnly: true },
    { icon: Mail, label: 'Email Recipients', path: '/portal/admin/email-recipients', adminOnly: true },
    { icon: Users, label: 'User Management', path: '/portal/admin/users', adminOnly: true },
    { icon: UserPlus, label: 'Access Requests', path: '/portal/admin/access-requests', adminOnly: true },
    { icon: FileText, label: 'Blog Management', path: '/portal/admin/blog', adminOnly: true },
    { icon: Presentation, label: 'Landing Pages', path: '/portal/admin/landing-pages', adminOnly: true },
    { icon: HelpCircle, label: 'FAQ Management', path: '/portal/admin/faqs', adminOnly: true },
    { icon: Globe, label: 'SEO', path: '/portal/admin/seo', adminOnly: true }
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <motion.div 
      initial={false}
      animate={{ width: collapsed ? 80 : 280 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="h-screen bg-white border-r border-gray-200 flex flex-col sticky top-0 shadow-sm"
    >
      {/* Logo & Collapse Button */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <AnimatePresence mode="wait">
          {!collapsed && (
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Link to="/">
                <img src="/ascarii-logo.png" srcSet="/ascarii-logo.png 1x, /ascarii-logo@2x.png 2x" alt="Ascarii" className="h-8" />
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
        <button 
          onClick={() => setCollapsed(!collapsed)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          {collapsed ? <ChevronRight className="w-5 h-5 text-gray-600" /> : <ChevronLeft className="w-5 h-5 text-gray-600" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <div className="space-y-1">
          {menuItems
            .filter(item => !item.adminOnly || user?.role === 'admin' || user?.role === 'super_admin')
            .map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleLinkClick}
                className={`flex items-center ${collapsed ? 'justify-center' : 'justify-start'} gap-3 px-3 py-3 rounded-lg transition-all group ${
                  isActive(item.path)
                    ? 'bg-gradient-to-r from-blue-600 to-sky-600 text-white shadow-md'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <item.icon className={`w-5 h-5 flex-shrink-0 ${isActive(item.path) ? 'text-white' : 'text-gray-600 group-hover:text-sky-600'}`} />
                <AnimatePresence mode="wait">
                  {!collapsed && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.2 }}
                      className={`font-medium text-sm whitespace-nowrap ${isActive(item.path) ? 'text-white' : ''}`}
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>
              </Link>
            ))}
        </div>
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-gray-200">
        <AnimatePresence mode="wait">
          {!collapsed ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="mb-3 p-3 bg-gray-50 rounded-lg"
            >
              <div className="text-xs font-semibold text-gray-900">{user?.first_name} {user?.last_name}</div>
              <div className="text-xs text-gray-500">{user?.email}</div>
              <div className="mt-2">
                <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                  {user?.role === 'super_admin' ? 'Super Admin' : user?.role === 'admin' ? 'Admin' : 'User'}
                </span>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
        
        <button
          onClick={() => {
            logout();
            navigate('/portal/login');
          }}
          className={`flex items-center ${collapsed ? 'justify-center w-full' : 'justify-start gap-3 w-full'} px-3 py-3 rounded-lg text-gray-700 hover:bg-red-50 hover:text-red-600 transition-all group`}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          <AnimatePresence mode="wait">
            {!collapsed && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className="font-medium text-sm whitespace-nowrap"
              >
                Logout
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>
    </motion.div>
  );
};

export default PortalSidebar;
